// Demo-mode (FakeRest) parity for batch-1 views and RPCs: companies_summary, v_timeline,
// trg_companies_normalize_phone, assign_lead, mark_do_not_call (import_companies: fakerestImport.ts).
// The functions receive the raw FakeRest provider (no lifecycle callbacks, no recursion).
import type {
  DataProvider,
  GetListParams,
  GetOneParams,
  Identifier,
} from "ra-core";
import fakeRestDataProvider from "ra-data-fakerest";
import { DEFAULT_MAX_ATTEMPTS } from "../domain/leadQueue";
import { summarizeLead, type SummaryInputs } from "../domain/leadSummary";
import { normalizePhone } from "../domain/phone";
import { buildTimeline, type TimelineSources } from "../domain/timeline";

export type Row = Record<string, unknown> & { id: Identifier };
const EVERYTHING = {
  pagination: { page: 1, perPage: 1_000_000 },
  sort: { field: "id", order: "ASC" as const },
  filter: {},
};
/** Every row of a collection; a collection the db does not define (older fixtures) is empty. */
export const all = async (
  dp: DataProvider,
  resource: string,
): Promise<Row[]> => {
  try {
    return (await dp.getList(resource, EVERYTHING)).data as Row[];
  } catch (error) {
    if (String((error as Error)?.message).startsWith("Undefined collection"))
      return [];
    throw error;
  }
};

/** The RPC exception (`raise exception 'x' using errcode = 'P0001'`) as the Supabase provider throws it. */
export const rpcError = (message: string) =>
  Object.assign(new Error(message), {
    code: "P0001",
    status: message === "forbidden" ? 403 : undefined,
  });
export const forbidden = () => rpcError("forbidden");

export const phoneSuppressions = (suppressions: Row[]) =>
  new Set(
    suppressions
      .filter((s) => s.channel === "phone")
      .map((s) => String(s.value)),
  );

export async function isAdmin(
  dp: DataProvider,
  me: number | null,
): Promise<boolean> {
  if (me === null) return false;
  const sales = await all(dp, "sales");
  const row = sales.find((s) => Number(s.id) === me);
  return row?.administrator === true && row.disabled !== true;
}

export const LEAD_CREATE_DEFAULTS = Object.freeze({
  prospect_status: "new",
  phone_valid: true,
  attempt_count: 0,
  high_quality: false,
  language: "fr",
  tags: [],
  country: "CA",
  timezone: "America/Toronto",
});

/** Mirror of trg_companies_normalize_phone + trg_companies_updated_at. */
export function normalizeLeadRecord(
  data: Record<string, unknown>,
  previous?: Record<string, unknown>,
): Record<string, unknown> {
  const out: Record<string, unknown> = {
    ...data,
    updated_at: new Date().toISOString(),
  };
  if (!("phone_number" in data)) return out;
  const e164 = normalizePhone(data.phone_number);
  out.phone_e164 = e164;
  const hasRaw =
    typeof data.phone_number === "string" && data.phone_number.trim() !== "";
  if (e164 === null && hasRaw) out.phone_valid = false;
  else if (
    e164 !== null &&
    (!previous || previous.phone_number !== data.phone_number)
  )
    out.phone_valid = true;
  return out;
}

async function summaryContext(dp: DataProvider, me: number | null) {
  const [sales, deals, tasks, contacts, suppressions, configuration] =
    await Promise.all(
      [
        "sales",
        "deals",
        "tasks",
        "contacts",
        "suppressions",
        "configuration",
      ].map((r) => all(dp, r)),
    );
  const config = configuration[0]?.config as
    | { gavion?: { max_attempts?: number } }
    | undefined;
  return {
    inputs: { sales, deals, tasks, contacts } as unknown as SummaryInputs,
    ctx: {
      me,
      now: new Date(),
      maxAttempts: config?.gavion?.max_attempts ?? DEFAULT_MAX_ATTEMPTS,
      suppressedPhones: phoneSuppressions(suppressions),
    },
  };
}

/** companies / companies_summary: every summary field, then FakeRest filters, sorts and paginates. */
export async function listLeads(
  dp: DataProvider,
  params: GetListParams,
  me: number | null,
) {
  const [companies, { inputs, ctx }] = await Promise.all([
    all(dp, "companies"),
    summaryContext(dp, me),
  ]);
  const rows = companies.map((c) => summarizeLead(c, inputs, ctx));
  return fakeRestDataProvider({ companies: rows }, false).getList(
    "companies",
    params,
  );
}

export async function getLead(
  dp: DataProvider,
  params: GetOneParams,
  me: number | null,
) {
  const { data } = await dp.getOne("companies", params);
  const { inputs, ctx } = await summaryContext(dp, me);
  return { data: summarizeLead(data as Row, inputs, ctx) };
}

const compare = (a: unknown, b: unknown, field: string): number => {
  const [x, y] =
    field === "at"
      ? [Date.parse(String(a)), Date.parse(String(b))]
      : [String(a ?? ""), String(b ?? "")];
  return x < y ? -1 : x > y ? 1 : 0;
};

/**
 * v_timeline, ordered like the view: the requested field, then the numeric part of the id
 * (source_id), then the id text, all in the same direction.
 */
export async function listTimeline(dp: DataProvider, params: GetListParams) {
  const [
    companyNotes,
    contactNotes,
    dealNotes,
    calls,
    tasks,
    contacts,
    deals,
    meetingNotes, // GAVION batch 6
    emailMessages, // GAVION batch 3: emails
  ] = await Promise.all(
    [
      "company_notes",
      "contact_notes",
      "deal_notes",
      "calls",
      "tasks",
      "contacts",
      "deals",
      "meeting_notes", // GAVION batch 6
      "email_messages", // GAVION batch 3: emails
    ].map((r) => all(dp, r)),
  );
  const items = buildTimeline({
    companyNotes,
    contactNotes,
    dealNotes,
    calls,
    tasks,
    contacts,
    deals,
    meetingNotes, // GAVION batch 6
    emailMessages, // GAVION batch 3: emails
  } as unknown as TimelineSources);
  const { data } = await fakeRestDataProvider(
    { v_timeline: items },
    false,
  ).getList("v_timeline", { ...EVERYTHING, filter: params.filter ?? {} });
  const field = params.sort?.field ?? "at";
  const dir = params.sort?.order === "ASC" ? 1 : -1;
  const sorted = [...data].sort(
    (a, b) =>
      dir *
      (compare(a[field], b[field], field) ||
        Number(a.source_id) - Number(b.source_id) ||
        compare(a.id, b.id, "id")),
  );
  const { page = 1, perPage = sorted.length } = params.pagination ?? {};
  return {
    data: sorted.slice((page - 1) * perPage, page * perPage),
    total: sorted.length,
  };
}

export async function checkLeadPhonesLocal(dp: DataProvider, phones: string[]) {
  const [companies, suppressions] = await Promise.all([
    all(dp, "companies"),
    all(dp, "suppressions"),
  ]);
  const known = new Set(companies.map((c) => c.phone_e164));
  const blocked = phoneSuppressions(suppressions);
  return {
    existing: phones.filter((p) => known.has(p)),
    suppressed: phones.filter((p) => blocked.has(p)),
  };
}

/** Twin of assign_lead: admins assign freely; a rep only to themself, and only unassigned rows change. */
export async function assignLeadsLocal(
  dp: DataProvider,
  ids: Identifier[],
  salesId: Identifier | null,
  me: number | null,
): Promise<number> {
  if (me === null) throw forbidden();
  const admin = await isAdmin(dp, me);
  if (!admin && (salesId === null || Number(salesId) !== me)) throw forbidden();
  const { data } = await dp.getMany("companies", { ids });
  const targets = admin
    ? data
    : data.filter((c) => c.assigned_sales_id == null);
  if (targets.length === 0) return 0;
  await dp.updateMany("companies", {
    ids: targets.map((c) => c.id),
    data: {
      assigned_sales_id: salesId === null ? null : Number(salesId),
      updated_at: new Date().toISOString(),
    },
  });
  return targets.length;
}

/** Twin of mark_do_not_call: status do_not_call plus a phone suppression. */
export async function markDoNotCallLocal(
  dp: DataProvider,
  ids: Identifier[],
  me: number | null,
): Promise<number> {
  if (!(await isAdmin(dp, me))) throw forbidden();
  const [{ data: companies }, suppressions] = await Promise.all([
    dp.getMany("companies", { ids }),
    all(dp, "suppressions"),
  ]);
  const blocked = phoneSuppressions(suppressions);
  const now = new Date().toISOString();
  for (const company of companies) {
    const phone = company.phone_e164 as string | null;
    if (!phone || blocked.has(phone)) continue;
    blocked.add(phone);
    await dp.create("suppressions", {
      data: {
        channel: "phone",
        value: phone,
        reason: "manual",
        source: "admin",
        company_id: company.id,
        created_by: me,
        created_at: now,
      },
    });
  }
  await dp.updateMany("companies", {
    ids: companies.map((c) => c.id),
    data: { prospect_status: "do_not_call", updated_at: now },
  });
  return companies.length;
}
